import { forwardRef, useCallback } from "react";
import type { ComponentPropsWithRef, MouseEventHandler } from "react";
import { Link, Section } from "@react-email/components";
import { omit } from "lodash";
import { useAttachmentMetadata } from "@/react/components/shared/BuilderUI/Media/AttachmentContext";
import type { StyleValue } from "@/react/components/shared/BuilderUI/StyleValue.types";
import type { BlockComponent } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import { useParseStyles } from "../../../Design/useParseStyles";
import { defaultStyles } from "./defaultStyles";

interface AttachmentBlockProps extends ComponentPropsWithRef<typeof Section> {
  attachment: {
    signed_id: string;
    url: string;
  };
  "data-preview-mode": boolean;
  styles: Record<string, StyleValue>;
  text: string;
}

export const AttachmentBlock: BlockComponent = forwardRef<
  HTMLTableElement,
  AttachmentBlockProps
>(
  (
    { text, attachment, "data-preview-mode": isPreviewMode, styles, ...rest },
    ref,
  ) => {
    const {
      margin: marginStyles,
      padding: paddingStyles,
      sizing: sizingStyles,
      textAlign: textAlignStyles,
      ...restStyles
    } = styles;
    const sectionStyles = useParseStyles({
      styles: {
        margin: marginStyles,
        padding: paddingStyles,
        sizing: sizingStyles,
        textAlign: textAlignStyles,
      },
      defaultStyles: {
        margin: defaultStyles.margin,
        padding: defaultStyles.padding,
      },
    });
    const inlineStyles = useParseStyles({
      styles: restStyles,
      defaultStyles: omit(defaultStyles, ["margin", "padding"]),
    });

    const attachmentMetadata = useAttachmentMetadata(attachment.signed_id);
    const { url } = attachmentMetadata ?? {};

    const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
      event => {
        if (isPreviewMode) event.preventDefault();
      },
      [isPreviewMode],
    );

    return (
      <Section ref={ref} style={sectionStyles} {...rest}>
        <Link
          href={url}
          target="_blank"
          onClick={handleClick}
          rel="noopener noreferrer"
          style={inlineStyles}
        >
          {text}
        </Link>
      </Section>
    );
  },
);

AttachmentBlock.displayName = "AttachmentBlock";
